// =============================================================================
// ui/tabs/DedupeTradesModal.tsx — Review & Remove duplicate trades.
// -----------------------------------------------------------------------------
// PORTED FROM `app/index.html:46686` (`openDedupeTradesModal`) — #73.
//
// ── 🚨 NOTHING IS DELETED THAT THE TRADER DID NOT TICK ──
//
// A shared signature is a CANDIDATE, not a verdict. Two same-minute, same-size
// scalps filled at the same price are a legitimate pair, and this screen cannot
// tell them apart from a double-import — only the person who took them can. So
// every group carries its own switch, the rows are shown with the reason one
// copy is being kept, and the total says what the button will do before it does
// it.
//
// ── 🚨 A HARD DELETE, SO THE ROWS LEAVE ON PAPER FIRST ──
//
// `deleteTrades` is a hard delete — there is no 30-day grace on `trades`, that
// pattern exists on accounts. The old client downloads a whole-state backup and
// tells the reader to re-import it from Settings → Data.
//
// ⚠ THIS CLIENT NOW HAS THAT TOO, as of TCR-41/42 — Settings → Backup & Restore
// holds a whole-book import and a per-file re-import. **The design here does not
// change, and the reason is worth keeping.** A whole-book restore puts back the
// state of the last backup, which silently discards every trade and annotation
// added since; pointing a user at it to undo a de-dupe would trade a small,
// deliberate deletion for a large, invisible one.
//
// ▶ So what downloads here is still the EXACT ROWS being removed, described as
// what it is: a record of what left, with everything needed to re-enter one by
// hand. Narrower than a backup, and it cannot cost anything that is not on
// screen.
//
// ⚠ AND IT DOWNLOADS BEFORE THE DELETE, not after. A failure between the two is
// then a file the trader has for nothing, which is the harmless direction.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSupabase } from '../state/supabase';
import { loadDuplicateDetail, type DupDetailTrade } from '../../io/sync/duplicateScan';
import { deleteTrades } from '../../io/sync/bulkEdit';
import {
  detectDuplicateTrades, planDedupeGroup, tradeRichness,
  type DupGroup,
} from '../../selectors/duplicateTrades';
import { money, num } from '../format';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface DedupeTradesModalProps {
  /** Every candidate id the scan found, across all groups. */
  candidateIds: readonly string[];
  /** Account id → the name to print. */
  accountName: (id: string) => string;
  onClose: () => void;
  /** Called after a successful removal, so the banner behind re-counts. */
  onRemoved: (count: number) => void;
}

type Phase = 'loading' | 'ready' | 'working' | 'failed';

export function DedupeTradesModal(
  { candidateIds, accountName, onClose, onRemoved }: DedupeTradesModalProps,
) {
  const { sb, userId } = useSupabase();
  const [phase, setPhase] = useState<Phase>('loading');
  const [rows, setRows] = useState<readonly DupDetailTrade[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [off, setOff] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const detail = await loadDuplicateDetail(sb, candidateIds);
        if (cancelled) return;
        setRows(detail);
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        setError(reportFailure('read duplicate trades', e));
        setPhase('failed');
      }
    })();
    return () => { cancelled = true; };
  }, [sb, candidateIds]);

  /**
   * ⚠ RE-GROUPED FROM THE DETAIL ROWS rather than trusting the scan's grouping.
   * The two reads are separate round trips, and anything that changed in between
   * — a trade edited on another tab, a row already deleted — must change what
   * this screen offers to delete. Re-running the same rule over the rows in hand
   * is the only version of this that cannot propose deleting a trade that is no
   * longer a duplicate.
   */
  const groups: readonly DupGroup<DupDetailTrade>[] = useMemo(
    () => detectDuplicateTrades(rows), [rows],
  );

  const plans = useMemo(
    () => new Map(groups.map((g) => [g.key, planDedupeGroup(g.trades)])),
    [groups],
  );

  const removeIds = useMemo(() => {
    const ids: string[] = [];
    for (const g of groups) {
      if (off.has(g.key)) continue;
      ids.push(...(plans.get(g.key)?.removeIds ?? []));
    }
    return ids;
  }, [groups, plans, off]);

  const toggle = useCallback((key: string) => {
    setOff((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const run = useCallback(async () => {
    if (!removeIds.length) return;
    setPhase('working');
    setError(null);

    // ⚠ The file first. See the header — a download the trader did not need is
    // harmless; a delete with no record of what went is not.
    const going = rows.filter((r) => removeIds.includes(r.id));
    try {
      const blob = new Blob(
        [JSON.stringify({
          kind: 'tradecoach-removed-duplicates',
          removedAt: new Date().toISOString(),
          note: 'These rows were removed as duplicates. This client cannot '
            + 're-import them — it is a record of what left, not a restore file.',
          trades: going,
        }, null, 2)],
        { type: 'application/json' },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tradecoach-removed-duplicates-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // ⚠ A BLOCKED DOWNLOAD MUST NOT BE A BLOCKED DELETE… and it must not be a
      // silent one either. The trader is told the record did not save and asked
      // to decide, rather than having the rows removed with no paper trail.
      setError(
        'The record of what would be removed could not be downloaded, so '
        + 'nothing was deleted. Check your browser’s download settings and try '
        + 'again.',
      );
      setPhase('ready');
      return;
    }

    const res = await deleteTrades(sb, userId, removeIds);
    if (res.error) {
      setError(res.error);
      setPhase('failed');
      // ⚠ STILL REPORTS THE PARTIAL. A chunked hard delete can stop halfway, and
      // the rows already gone are gone — the banner behind must re-count them.
      if (res.deleted) onRemoved(res.deleted);
      return;
    }
    onRemoved(res.deleted);
    onClose();
  }, [removeIds, rows, sb, userId, onRemoved, onClose]);

  const activeGroups = groups.filter((g) => !off.has(g.key)).length;

  return createPortal(
    <div className="modal-scrim" role="dialog" aria-modal="true"
      aria-label="Review and remove duplicate trades">
      <div className="modal wide">
        <header className="modal-head">
          <h3>Remove duplicate trades</h3>
          <button type="button" className="btn" onClick={onClose}
            aria-label="Close">×</button>
        </header>

        <div className="modal-body">
          <p className="muted">
            These trades share the same time, instrument, side, <b>size</b> and
            fill prices inside one account — usually a double-import. For each
            group the <b>copy carrying the most</b> is kept and the rest removed.
            <b> Untick any group that is really separate trades</b> — two
            same-minute scalps at the same price look identical here and are not.
          </p>

          {/* 🚨 THE ONE THING THIS TOOL CANNOT DO, said before the trader tries.
              Two loggers on one account split the same position into different
              numbers of fills, so there is no row-for-row match to find (D143:
              65 rows from three sources for one session, a day reading −$130
              that was −$1,462). Naming the screen that DOES fix it matters —
              this client has Undo import, so the sentence is true here. */}
          <p className="muted dedupe-warn">
            ⚠ <b>This is not the tool for two loggers on one account.</b> If
            Sierra and ATAS both wrote the same session they split your positions
            differently and no two rows will match. Remove one whole import
            instead, from <b>Trades → Import history → Undo import</b>.
          </p>

          {phase === 'loading' ? (
            <p className="muted" role="status">Reading those trades…</p>
          ) : null}

          {error ? <div className="cmp-error" role="alert">{error}</div> : null}

          {phase !== 'loading' && !groups.length ? (
            <p className="muted">
              No duplicates left to remove.
            </p>
          ) : null}

          {groups.map((g) => {
            const plan = plans.get(g.key);
            const on = !off.has(g.key);
            const first = g.trades[0];
            const sorted = g.trades.slice().sort(
              (a, b) => (a.id === plan?.keepId ? -1 : 0) - (b.id === plan?.keepId ? -1 : 0),
            );
            return (
              <div key={g.key} className="dedupe-group">
                <label className="dedupe-head">
                  <input type="checkbox" checked={on}
                    onChange={() => toggle(g.key)} />
                  <span>
                    <b>{accountName(g.accountId)}</b> · {first?.date ?? ''}{' '}
                    {first?.timeIn ?? ''} · {num(g.trades.length, 0)} copies
                  </span>
                  <span className="grow" />
                  <span className="muted">
                    {on ? `remove ${num(plan?.removeIds.length ?? 0, 0)}` : 'keep all'}
                  </span>
                </label>
                <div className={on ? 'dedupe-rows' : 'dedupe-rows off'}>
                  {sorted.map((t) => {
                    const keep = t.id === plan?.keepId;
                    const rich = tradeRichness(t);
                    return (
                      <div key={t.id} className="dedupe-row">
                        <span className={keep ? 'dedupe-keep' : 'dedupe-drop'}>
                          {keep ? 'keep' : 'remove'}
                        </span>
                        <span>
                          {t.contract ?? '?'} {t.side ?? ''} · qty{' '}
                          {t.qty == null ? '?' : num(t.qty, 0)} · {t.entry}→{t.exit}
                          {' · '}{money(t.netPnl, 2)}
                          {rich ? ` · ${num(rich, 0)} field${rich === 1 ? '' : 's'} kept` : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {groups.length ? (
            <div className="dedupe-total">
              Total: <b>{num(removeIds.length, 0)}</b>{' '}
              duplicate trade{removeIds.length === 1 ? '' : 's'} removed across{' '}
              <b>{num(activeGroups, 0)}</b> group{activeGroups === 1 ? '' : 's'}
            </div>
          ) : null}
        </div>

        <footer className="modal-foot">
          <span className="grow" />
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary"
            disabled={phase !== 'ready' || !removeIds.length}
            onClick={() => { void run(); }}
          >
            {phase === 'working' ? 'Removing…' : 'Download the rows & remove'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
